import { useMemo } from 'react';
import { COMPONENT_POSITION, INVISIBLES } from '@workspace/constants';
import { getBackgroundClass, shouldOpenInNewTab } from '@workspace/lib';
import { mapHeadingVariant, processHeading } from '@workspace/lib/heading-utils';
import { openDocsBot } from '@workspace/lib/docsbot';
import { ProductSummaryBlock, CTAItem } from './product-summary.types';
import { typographyCva } from '@workspace/ui/common';

export const IMAGE_DIMENSIONS = {
  width: 544,
  height: 544,
  className: 'md:w-[544px] md:h-[544px] h-full',
} as const;

export function useProductSummary(block: ProductSummaryBlock) {
  const {
    isActive = true,
    backgroundColor,
    headline,
    description,
    image,
    imagePosition = 'left',
    ctaUnderHeadline = [],
    ctaUnderDescription = [],
    isVideo,
    video,
  } = block;

  const resolvedHeading = useMemo(() => {
    const { text: resolvedHeadingText } = processHeading(headline);
    const headingVariant = mapHeadingVariant(headline, 'h2');
    const headingFontClass = typographyCva({ variant: headingVariant });
    return { resolvedHeadingText, headingVariant, headingFontClass };
  }, [headline]);

  const cleanImagePosition = useMemo(() => {
    return typeof imagePosition === 'string' ? imagePosition.replace(INVISIBLES, '').trim().toLowerCase() : 'left';
  }, [imagePosition]);

  const backgroundClass = useMemo(() => getBackgroundClass(backgroundColor), [backgroundColor]);

  const hasMedia = useMemo(() => !!(isVideo && video) || !!(image?.asset?._ref || image?.url), [isVideo, video, image]);

  const imageOrderClass = useMemo(
    () => (cleanImagePosition === COMPONENT_POSITION.POSITION_RIGHT ? 'md:order-3' : 'order-1'),
    [cleanImagePosition],
  );

  const contentOrderClass = useMemo(
    () => (cleanImagePosition === COMPONENT_POSITION.POSITION_RIGHT ? 'md:order-1' : 'order-3'),
    [cleanImagePosition],
  );

  const handleAskGregClick = () => {
    openDocsBot();
  };

  const getOpenInNewTab = (item: CTAItem): boolean => {
    return shouldOpenInNewTab({
      type: item.linkType,
      isOpenInNewTab: item.openInNewTab,
    });
  };

  return {
    isActive,
    resolvedHeading,
    cleanImagePosition,
    backgroundClass,
    hasMedia,
    imageOrderClass,
    contentOrderClass,
    handleAskGregClick,
    getOpenInNewTab,
    description,
    ctaUnderHeadline,
    ctaUnderDescription,
    isVideo,
    video,
    image,
    headline,
  };
}
